import { NgModule } from '@angular/core';
import { ExtraOptions, mapToCanActivate, mapToCanMatch, RouterModule, Routes, TitleStrategy } from '@angular/router';
import { AuthSsoLogoutComponent, CodeComponent } from '@kauth';
import { AuthenticatedGuard } from '@ki/auth/guards/authenticated.guard';
import { checkAdminStatusGuard } from '@ki/auth/guards/check-admin-status.guard';
import { checkSubscriptionStatusGuard } from '@ki/auth/guards/check-subscription-status.guard';
import { FeatureGuard } from '@ki/auth/guards/feature.guard';
import { NotAuthenticatedGuard } from '@ki/auth/guards/not-authenticated.guard';
import { RoleGuard } from '@ki/auth/guards/role.guard';
import { ScreenLogoutComponent } from '@ki/screen-logout/screen-logout.component';
import { TitleStrategyDefaultService } from '@kservice';
import { Feature, Role } from '@ktypes/enums';
import { ScreenSiteMapComponent } from '@kui';

const routerOptions: ExtraOptions = {
  anchorScrolling: 'enabled',
  bindToComponentInputs: true,
  canceledNavigationResolution: 'computed', // attempt to return to the same index in the session history if navigation cancelled
  // preloadingStrategy: PreloadAllModules, // uncomment to disable Lazy Loading of modules
  // enableTracing: true, // <-- debugging purposes only
};

const routes: Routes = [
  {
    path: 'auth/sso',
    component: CodeComponent,
  },
  {
    path: 'auth/sso/logout',
    component: AuthSsoLogoutComponent,
  },
  {
    path: 'login',
    loadChildren: () => import('./screen-login/screen-login.module').then((mod) => mod.ScreenLoginModule),
  },
  {
    path: 'logout',
    component: ScreenLogoutComponent,
    title: 'Logout',
  },
  {
    path: 'error',
    loadChildren: () => import('./screen-error/screen-error.module').then((mod) => mod.ScreenErrorModule),
  },
  {
    path: 'admin',
    loadChildren: () => import('./admin/routes').then((mod) => mod.ADMIN_ROUTES),
    canActivate: [checkAdminStatusGuard()],
    canMatch: mapToCanMatch([RoleGuard]),
    data: { requiredRoles: [Role.INSIGHTFUL_ADMIN] },
  },
  {
    path: 'onboarding',
    loadChildren: () => import('./onboarding/routes').then((mod) => mod.ONBOARDING_ROUTES),
  },
  {
    path: 'dashboard',
    loadChildren: () => import('./screen-dashboard/screen-dashboard.module').then((mod) => mod.ScreenDashboardModule),
    canActivate: [...mapToCanActivate([AuthenticatedGuard]), checkAdminStatusGuard(), checkSubscriptionStatusGuard()],
    canMatch: mapToCanMatch([FeatureGuard]),
    data: { requiredFeatures: [Feature.INSIGHTFUL] },
  },
  {
    path: 'detail',
    loadChildren: () => import('./screen-dashboard/screen-dashboard.module').then((mod) => mod.ScreenDashboardModule),
    canActivate: [...mapToCanActivate([AuthenticatedGuard]), checkAdminStatusGuard(), checkSubscriptionStatusGuard()],
    canMatch: mapToCanMatch([FeatureGuard]),
    data: { requiredFeatures: [Feature.INSIGHTFUL] },
  },
  {
    path: 'reports',
    loadChildren: () => import('./screen-reports/screen-reports.module').then((mod) => mod.ScreenReportsModule),
    canActivate: [...mapToCanActivate([AuthenticatedGuard]), checkAdminStatusGuard(), checkSubscriptionStatusGuard()],
    canMatch: mapToCanMatch([FeatureGuard]),
    data: { requiredFeatures: [Feature.INSIGHTFUL] },
  },
  {
    path: 'kumanu-admin',
    loadChildren: () =>
      import('@ki/screen-kumanu-admin/screen-kumanu-admin.module').then((mod) => mod.ScreenKumanuAdminModule),
    canActivate: [...mapToCanActivate([AuthenticatedGuard]), checkAdminStatusGuard()],
    canMatch: mapToCanMatch([FeatureGuard, RoleGuard]),
    data: {
      requiredFeatures: [Feature.INSIGHTFUL],
      requiredRoles: [Role.KUMANU_INSIGHTFUL_ADMIN],
    },
  },
  {
    path: 'site-map',
    component: ScreenSiteMapComponent,
    canActivate: [checkAdminStatusGuard(), checkSubscriptionStatusGuard()],
    title: 'Site Map',
  },
  {
    path: '',
    pathMatch: 'full',
    // need empty children array to allow for canActivate;
    // need one of component, children or redirectTo defined per Angular spec
    children: [],
    canActivate: [
      ...mapToCanActivate([NotAuthenticatedGuard]),
      checkAdminStatusGuard(),
      checkSubscriptionStatusGuard(),
    ],
  },
  { path: '**', redirectTo: '/error/notFound' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, routerOptions)],
  exports: [RouterModule],
  providers: [{ provide: TitleStrategy, useClass: TitleStrategyDefaultService }],
})
export class AppRoutingModule {}
